import { CheckboxInput, Modal, type ModalProps, TextInput } from '@app/components'
import { useChangeMfa, useMfa, useSetPhoneNumber, useVerifyPhoneNumber } from '@app/lib'
import { CognitoMfaType } from '@app/types'
import { Alert, Anchor, Button, Stack, Text } from '@mantine/core'
import { useForm, yupResolver } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { IconAlertCircle, IconCheck } from '@tabler/icons-react'
import phone from 'phone'
import { type FC, useCallback, useRef, useState } from 'react'
import * as yup from 'yup'

const validatePhoneForm = yupResolver(
  yup.object({
    phoneNumber: yup.string().phone('Must be a valid phone number').required('Phone number is required'),
    terms: yup.boolean().isTrue('You must agree to the terms to enable SMS.')
  })
)
const validateCodeForm = yupResolver(
  yup.object({
    code: yup.string().matches(/[0-9]{6}/, {
      message: 'Must be exactly 6 digits long'
    })
  })
)

export interface SmsSetupModalProps extends Omit<ModalProps, 'children'> {}

export const SmsSetupModal: FC<SmsSetupModalProps> = ({ onClose, ...props }) => {
  const [error, setError] = useState<string | null>(null)
  const [needsVerify, setNeedsVerify] = useState(false)
  const codeInputRef = useRef<HTMLInputElement>(null)
  const { data: mfa } = useMfa()
  const { isPending: isChangingMfa, mutateAsync: changeMfa } = useChangeMfa()
  const { isPending: isSettingPhoneNumber, mutateAsync: setPhoneNumber } = useSetPhoneNumber()
  const { isPending: isVerifyingPhoneNumber, mutateAsync: verifyPhoneNumber } = useVerifyPhoneNumber()
  const codeForm = useForm({
    initialValues: {
      code: ''
    },
    validate: validateCodeForm,
    validateInputOnBlur: true,
    validateInputOnChange: true
  })
  const phoneForm = useForm({
    initialValues: {
      phoneNumber: '',
      terms: false
    },
    validate: validatePhoneForm,
    validateInputOnBlur: true,
    validateInputOnChange: true
  })

  const handleClose = useCallback(() => {
    codeForm.reset()
    phoneForm.reset()
    setError(null)
    setNeedsVerify(false)
    onClose()
  }, [codeForm, phoneForm, onClose])
  const onFocus = useCallback(() => setError(null), [])
  const onPhoneSubmit = useCallback(async () => {
    const phoneResult = phone(phoneForm.values.phoneNumber)

    if (phoneResult.isValid) {
      const { success, message } = await setPhoneNumber(phoneResult.phoneNumber)

      if (success) {
        setNeedsVerify(true)
        setTimeout(() => codeInputRef.current?.focus(), 50)
      } else if (message) {
        setError(message)
      }
    } else {
      phoneForm.setFieldError('phoneNumber', 'Invalid phone number')
    }
  }, [phoneForm, setPhoneNumber])
  const onCodeSubmit = useCallback(async () => {
    const { error, success, message } = await verifyPhoneNumber(codeForm.values.code)

    if (success) {
      await changeMfa({
        enabled: [...(mfa?.enabled ?? []), CognitoMfaType.SMS],
        preferred: CognitoMfaType.SMS
      })

      notifications.show({
        color: 'green',
        icon: <IconCheck />,
        message,
        withBorder: true,
        withCloseButton: false
      })

      handleClose()
    } else if (error?.name === 'CodeMismatchException') {
      setError('Invalid code, please try again.')
    } else if (message) {
      setError(message)
    }
  }, [changeMfa, codeForm.values, handleClose, mfa?.enabled, verifyPhoneNumber])

  return (
    <Modal
      keepMounted={false}
      isLoading={isSettingPhoneNumber || isVerifyingPhoneNumber || isChangingMfa}
      onClose={handleClose}
      size='sm'
      title='SMS Setup'
      {...props}
    >
      <Stack spacing='md'>
        {error && (
          <Alert
            color='red.6'
            icon={<IconAlertCircle />}
          >
            {error}
          </Alert>
        )}
        {needsVerify ? (
          <>
            <TextInput
              placeholder='Code'
              label='Enter the verification code received to activate SMS'
              onFocus={onFocus}
              ref={codeInputRef}
              w='100%'
              {...codeForm.getInputProps('code')}
            />
            <Button
              disabled={isVerifyingPhoneNumber || isChangingMfa || !codeForm.isValid()}
              fullWidth
              onClick={onCodeSubmit}
            >
              Verify
            </Button>
          </>
        ) : (
          <>
            <Text>Each time you log in, we’ll send a code to this number.</Text>
            <TextInput
              data-autofocus
              label='Enter your phone number'
              placeholder='+13035551212'
              onFocus={onFocus}
              w='100%'
              {...phoneForm.getInputProps('phoneNumber')}
            />
            <CheckboxInput
              label={
                <Text>
                  I agree to receive recurring automated informational SMS messages from MetrixIQ, LLC to the number
                  provided regarding status of the Service and security confirmations. Msg and data rates may apply. Opt
                  out by replying STOP. Not a condition of participation. Msg frequency varies.{' '}
                  <Anchor
                    target='_blank'
                    href='https://www.metrixiq.com/privacy-policy'
                  >
                    Privacy Policy
                  </Anchor>
                  .
                </Text>
              }
              {...phoneForm.getInputProps('terms', { type: 'checkbox' })}
            />
            <Button
              disabled={isSettingPhoneNumber || !phoneForm.isValid()}
              fullWidth
              onClick={onPhoneSubmit}
            >
              Send Verification Code
            </Button>
          </>
        )}
      </Stack>
    </Modal>
  )
}
